/* global window */
const fallbackReporter: ReporterFn = (
  errorName,
  message,
  metadata,
  {skipConsole = process.env.NODE_ENV === 'production'} = {}
) => {
  if (skipConsole !== true) {
    console.error(errorName, message, metadata);
  }
};
const reportedMessages = new Set();

type Priority = 'warning' | 'error' | 'fatal';

// NOTE: This data is enforced via the eslint rule webflow/better-bug-reporting, however they are marked
// optional here so the existing callsites don't need to be flow ignored.
export type Metadata = {
  team?: string | Array<string>;
  ticket?: string;
  priority?: Priority;
  [key: string]: any;
};

export type Params = {
  severity?: 'info' | 'warning' | 'error';
  skipConsole?: boolean;
};

export type ReporterFn = (
  errorName: string,
  message: string,
  metadata?: Metadata | null | undefined,
  params?: Params
) => void;

export const reportBug: ReporterFn = (errorName, message, metadata, params) => {
  if (typeof window !== 'undefined' && window._reportBug) {
    window._reportBug(errorName, message, metadata, params);
  } else {
    fallbackReporter(errorName, message, metadata, params);
  }
};

export const reportBugOnce: ReporterFn = (
  errorName,
  message,
  metadata,
  params
) => {
  if (reportedMessages.has(message)) {
    return;
  }

  reportedMessages.add(message);
  // eslint-disable-next-line webflow/better-bug-reporting
  reportBug(errorName, message, metadata, params);
};

export const reportException = (
  exception: Error,
  metadata: Metadata,
  {skipConsole = false, severity}: Params = {}
) => {
  if (typeof window !== 'undefined' && window._reportException) {
    window._reportException(exception, metadata, {
      skipConsole,
      severity,
    });
  } else {
    fallbackReporter(
      exception.name,
      exception.message,
      {
        metadata,
        stack: exception.stack,
      },
      {
        skipConsole,
      }
    );
  }
};

export const fatalError = (err: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(
      '@packages/utilities/reporter looks to be unreachable. If you see this ' +
        'please ping #frontend with the stacktrace.'
    );
    console.trace();
  }

  if (typeof window === 'undefined') {
    return;
  }

  if (
    /wfdev\.io:3000\/tests\.html/.test(window.location.toString()) ||
    /localhost:3000\/tests\.html/.test(window.location.toString())
  ) {
    return;
  }

  if (
    typeof window.SyncUtils === 'object' &&
    typeof window.SyncUtils.fatalError === 'function'
  ) {
    window.SyncUtils.fatalError(err);
  }
};

export default {
  reportBug,
  reportBugOnce,
  reportException,
  fatalError,
};
